import { Component, Input, forwardRef } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { DateTime } from 'luxon'
import { IsoDate, todayIso } from 'src/app/domain/dates'
import { QUARTERS, q15 } from 'src/app/domain/time'

/** A native select of the 96 quarter hours. The control value stays the DSR's ISO datetime string:
 *  it reads HH:mm (rounded to the quarter) and writes `{day}T{HH:mm}` as a local ISO datetime. */
@Component({
  selector: 'rt-quarter-select',
  template: `
    <select class="rt-fill rt-fill--select" [disabled]="disabled" [attr.aria-label]="label || null"
            (change)="pick($any($event.target).value)" (blur)="onTouched()">
      <option value="" [selected]="!hhmm">{{ placeholder }}</option>
      <option *ngFor="let q of quarters" [value]="q.value" [selected]="q.value === hhmm">{{ q.label }}</option>
    </select>`,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RtQuarterSelectComponent), multi: true }],
})
export class RtQuarterSelectComponent implements ControlValueAccessor {
  @Input() day: IsoDate | null = null
  @Input() placeholder = '—'
  @Input() label = ''

  readonly quarters = QUARTERS
  hhmm = ''
  disabled = false
  private iso: string | null = null
  private onChange: (iso: string | null) => void = () => {}
  onTouched: () => void = () => {}

  writeValue(iso: string | null): void {
    this.iso = iso
    const t = iso ? DateTime.fromISO(iso) : null
    this.hhmm = t?.isValid ? q15(t.toFormat('HH:mm')) : ''
  }

  registerOnChange(fn: (iso: string | null) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled
  }

  pick(hhmm: string) {
    this.hhmm = hhmm
    const current = this.iso ? DateTime.fromISO(this.iso) : null
    const day = this.day ?? (current?.isValid ? current.toISODate() : null) ?? todayIso()
    this.iso = hhmm ? DateTime.fromISO(`${day}T${hhmm}`).toISO() : null
    this.onChange(this.iso)
  }
}
